import { Box, ButtonBase } from '@mui/material'
import { YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'
import { ScrollRow } from '../design'
import { bannerActionLabel, type BannerDestination, type HomeBanner } from '../home/banners'

interface Props {
  banners: readonly HomeBanner[]
  /** Called only for a banner that resolved a destination. */
  onSelect: (destination: BannerDestination) => void
}

const TILE = {
  width: 300,
  height: 136,
  borderRadius: `${YALO_RADIUS.lg}px`,
  overflow: 'hidden',
  // The artwork is remote and may be slow, transparent or missing; without a
  // surface underneath, the rail is a row of holes in the page while it loads.
  bgcolor: YALO_SEMANTIC.bgSunken,
} as const

/** Fills whatever box it is in without stretching the artwork. */
const FILL = { objectFit: 'cover', display: 'block' } as const

/**
 * The storefront's own banners, as a scrolling rail under the search bar —
 * which is where cinnamon puts them too (`CarouselBanners`, the first thing
 * under the catalogue header).
 *
 * A fixed tile rather than a full-width hero: the home already carries the
 * reels, the suggested basket and the category chips above the grid, and a 2:1
 * banner across the column would cost another ~240px of the first screen. At
 * this size the next tile peeks, which is also what says the rail scrolls.
 *
 * Some banners are buttons and some are plain images, and the data decides
 * which: one whose `reference` names somewhere this storefront can actually go
 * is tappable, one whose does not is a picture. Rendering the second as a
 * `ButtonBase` would be the worse lie — it takes focus, it announces itself as
 * a control, and it never acts.
 *
 * **Alt text.** Every image here is `alt=""`. There is no alt text anywhere in
 * the banner data and this component cannot see the artwork, so any description
 * it produced would be invented — the one thing this codebase does not do. The
 * tappable ones are not left nameless: the *button* is named after where it
 * goes (`bannerActionLabel`), which is both true and the only part a
 * screen-reader user can act on. The untappable ones carry nothing such a
 * shopper could use, so `alt=""` keeps them out of the accessibility tree
 * instead of announcing "image" once per banner.
 */
export const BannerRail = ({ banners, onSelect }: Props) => (
  <ScrollRow>
    {banners.map(({ id, imageUrl, destination }) =>
      destination === null ? (
        <Box
          key={id}
          component="img"
          src={imageUrl}
          alt=""
          data-testid="banner-image"
          sx={{ ...TILE, ...FILL }}
        />
      ) : (
        <ButtonBase
          key={id}
          onClick={() => onSelect(destination)}
          aria-label={bannerActionLabel(destination)}
          sx={{ ...TILE, display: 'block', '&:active': { transform: 'scale(0.99)' } }}
        >
          <Box
            component="img"
            src={imageUrl}
            alt=""
            data-testid="banner-image"
            sx={{ width: '100%', height: '100%', ...FILL }}
          />
        </ButtonBase>
      ),
    )}
  </ScrollRow>
)
